// SQUIDDI-ricerca - 2026-10-08
// Ricerca del catalogo tollerante ai refusi (GET /search/products, rapidfuzz ≥70%),
// nessuna AI. «Vedi tutti i risultati» porta su /shop?cat=all&q=…
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Sparkles } from "lucide-react";
import { getProducts, getCategories, searchProducts } from "@/lib/api";
import { imgUrl, IMG_FALLBACK, euro } from "@/lib/format";
import { toGross } from "@/lib/vat";
import { track } from "@/lib/track";

let CACHE = null;          // catalogo caricato una volta per sessione di pagina
let CATS = null;

export default function HeaderSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!CACHE) getProducts({}).then((ps) => { CACHE = ps || []; setItems(CACHE); }).catch(() => { CACHE = []; });
    else setItems(CACHE);
    if (!CATS) getCategories().then((cs) => { CATS = (cs || []).filter((c) => c.active); setCats(CATS); }).catch(() => {});
    else setCats(CATS);
  }, []);

  const openSearch = () => { setOpen(true); load(); track("ricerca_aperta", {}); setTimeout(() => inputRef.current?.focus(), 60); };
  const closeSearch = useCallback(() => { setOpen(false); setQ(""); setActive(-1); setTimeout(() => btnRef.current?.focus(), 60); }, []);

  // Ricerca tollerante ai refusi lato server (prima esatti, poi simili ≥70%).
  const [results, setResults] = useState([]);
  useEffect(() => {
    const term = q.trim();
    if (!open || term.length < 2) { setResults([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      searchProducts(term, 6).then((hits) => {
        if (!alive) return;
        const bySlug = new Map((items || CACHE || []).map((p) => [p.slug, p]));
        setResults(hits.map((h) => bySlug.get(h.slug)).filter(Boolean));
      }).catch(() => alive && setResults([]));
    }, 160);
    return () => { alive = false; clearTimeout(t); };
  }, [q, items, open]);

  // Misura: una sola volta per ricerca, mezzo secondo dopo l'ultima lettera.
  useEffect(() => {
    if (!open || q.trim().length < 2) return;
    const t = setTimeout(() => {
      track("ricerca_eseguita", { q: q.trim().slice(0, 40), results: results.length });
      if (!results.length) track("ricerca_nessun_risultato", { q: q.trim().slice(0, 40) });
    }, 500);
    return () => clearTimeout(t);
  }, [q, open, results.length]);

  const goAll = () => { closeSearch(); navigate(`/shop?cat=all&q=${encodeURIComponent(q.trim())}`); };
  const goProduct = (slug) => { track("ricerca_risultato_cliccato", { slug }); closeSearch(); navigate(`/prodotto/${slug}`); };
  const askAi = () => {
    closeSearch();
    window.dispatchEvent(new CustomEvent("open-squiddi-ai", { detail: { prefill: q.trim() } }));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); closeSearch(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && results[active]) goProduct(results[active].slug);
      else if (q.trim().length >= 2) goAll();
    }
  };

  return (
    <>
      <button ref={btnRef} type="button" onClick={openSearch} data-testid="header-search-btn" aria-label="Cerca"
        aria-expanded={open}
        className="grid h-11 w-11 place-items-center rounded-full text-[var(--n-800)] transition-colors hover:bg-[var(--p-50)] hover:text-[var(--brand)]">
        <Search size={20} />
      </button>

      {open && (
        <>
          <div onClick={closeSearch} aria-hidden data-testid="header-search-backdrop"
            className="fixed inset-0 z-[79] bg-[#0B0814]/35" />
          <div role="dialog" aria-modal="true" aria-label="Cerca nel catalogo" data-testid="header-search-panel"
            className="fixed inset-x-0 top-0 z-[80] border-b border-[var(--n-200)] bg-white shadow-[0_20px_50px_-30px_rgba(11,8,20,.6)]">
            <div className="container-x flex h-[72px] items-center gap-2">
              <Search size={20} className="shrink-0 text-[var(--brand)]" />
              <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setActive(-1); }}
                onKeyDown={onKeyDown} data-testid="header-search-input" autoComplete="off"
                placeholder="Cerca un capo, una categoria, un colore…" aria-label="Cerca nel catalogo"
                className="min-w-0 flex-1 bg-transparent text-[15px] font-semibold outline-none placeholder:text-[var(--n-400)]" />
              <button type="button" onClick={closeSearch} data-testid="header-search-close" aria-label="Chiudi la ricerca"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-[var(--n-600)] hover:bg-[var(--n-100)]">
                <X size={20} />
              </button>
            </div>

            {q.trim().length >= 2 && (
              <div className="container-x max-h-[70dvh] overflow-y-auto border-t border-[var(--n-200)] py-2.5" data-testid="header-search-results">
                {results.length > 0 ? (
                  <>
                    {/* Telefono: fila di schede da sfogliare di lato; da tablet in su: elenco. */}
                    <ul data-testid="header-search-list"
                      className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-px-5 px-5 pb-1.5 sm:-mx-8 sm:scroll-px-8 sm:px-8 md:mx-0 md:block md:snap-none md:overflow-visible md:px-0 md:pb-0 md:divide-y md:divide-[var(--n-100)]">
                      {results.map((p, i) => (
                        <li key={p.slug} className="w-[38%] shrink-0 snap-start md:w-auto">
                          <button type="button" onClick={() => goProduct(p.slug)} data-testid={`search-result-${p.slug}`}
                            aria-selected={active === i}
                            className={`flex w-full flex-col gap-1.5 rounded-xl text-left transition-colors md:flex-row md:items-center md:gap-3 md:rounded-none md:px-1 md:py-2 ${active === i ? "bg-[var(--p-50)]" : "hover:bg-[var(--n-50)]"}`}>
                            <img src={imgUrl((p.colors || [{}])[0]?.image_main || p.images?.[0], 400) || IMG_FALLBACK} alt=""
                              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
                              className="aspect-[4/5] w-full shrink-0 rounded-xl border border-[var(--n-200)] bg-[var(--n-50)] object-cover md:aspect-auto md:h-12 md:w-12" />
                            <span className="min-w-0 flex-1">
                              <span className="line-clamp-2 min-h-[2.75em] font-display text-[13px] font-bold leading-snug md:block md:min-h-0 md:truncate md:text-[14px]">{p.name}</span>
                              <span className="hidden text-[11.5px] font-semibold text-[var(--n-500)] md:block">{p.category}</span>
                            </span>
                            <span className="shrink-0 font-display text-[13px] font-black text-[var(--brand)] tabular-nums md:text-[13.5px]">
                              da €{euro(toGross(p.price_from))}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button type="button" onClick={goAll} data-testid="header-search-all"
                      className="mt-1.5 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-[var(--p-50)] text-[13.5px] font-black text-[var(--brand)] transition-colors hover:bg-[var(--p-100)]">
                      Vedi tutti i risultati <ArrowRight size={15} />
                    </button>
                  </>
                ) : (
                  <div className="py-2" data-testid="header-search-empty">
                    <div className="text-[14px] font-bold">Nessun risultato per «{q.trim()}»</div>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {cats.slice(0, 3).map((c) => (
                        <Link key={c.slug} to={`/shop?cat=${c.slug}`} onClick={closeSearch}
                          data-testid={`header-search-suggest-${c.slug}`}
                          className="inline-flex min-h-[40px] items-center rounded-full border border-[var(--n-300)] px-4 text-[13px] font-bold hover:border-[var(--brand)] hover:text-[var(--brand)]">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                    <button type="button" onClick={askAi} data-testid="header-search-ask-ai"
                      className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[var(--brand)] px-5 text-[13.5px] font-black text-white hover:bg-[var(--brand-3)]">
                      <Sparkles size={15} /> Chiedi a Squiddi
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
